import { useResetOnClose } from "@/lib/useResetOnClose";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Warehouse as WarehouseIcon, PackageMinus, Share2 } from "lucide-react";
import { api } from "@/lib/api";
import { WbsItemSelect } from "@/components/procurement/WbsItemSelect";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { todayISO } from "@/lib/format";
import { useProjectCtx } from "@/context/ProjectContext";
import { PageHeader } from "@/components/PageHeader";
import { PageStatCards } from "@/components/PageStatCards";
import { TabFilter } from "@/components/TabFilter";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { getErrorMessage } from "@/lib/errors";
import { SurplusBoardTab, SurplusRequestsTab, OfferDialog } from "@/components/project/SurplusTabs";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const qty = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const dmy = (d) => (d ? d.split("-").reverse().join("-") : "—");

const KIND_TONE = { "Kept in site store": "default", "Used at once": "secondary", "Asset / machine": "outline" };
const KIND_STYLE = {
  "Kept in site store": { cls: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400", tip: "Kept in the site store: counts in stock and can be issued to work later" },
  "Used at once": { cls: "border-border bg-muted text-muted-foreground", tip: "Went straight to work on delivery: not in stock" },
  "Asset / machine": { cls: "border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400", tip: "Asset or machine: tracked under Assets, not stock" },
};

function Grid({ heads, rows, empty }) {
  if (!rows.length) return <div className="p-4"><EmptyState title={empty} /></div>;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow>{heads.map((h) => <TableHead key={h.label} className={h.right ? "text-right" : ""}>{h.label}</TableHead>)}</TableRow></TableHeader>
        <TableBody>
          {rows.map((cells, i) => (
            <TableRow key={i}>{cells.map((c, k) => <TableCell key={k} className={`${heads[k].right ? "text-right font-mono" : ""} text-xs`}>{c}</TableCell>)}</TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

export default function SiteInventory() {
  const { projectId } = useProjectCtx();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("stock");
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [issuing, setIssuing] = useState(null);
  const [offering, setOffering] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get(`/projects/${projectId}/site-inventory`);
      setData(res);
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { if (projectId) load(); }, [projectId, load]);

  const createStore = async () => {
    setCreating(true);
    try {
      const { data: r } = await api.post(`/projects/${projectId}/site-inventory/store`);
      toast.success(r.created ? `Site store ${r.name} created` : "This project already has a site store");
      load();
    } catch (err) { toast.error(getErrorMessage(err)); } finally { setCreating(false); }
  };

  if (!data) return <div className="flex justify-center p-10"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  const s = data.summary;
  const noStore = data.stores.length === 0;

  const tabs = [
    { key: "stock", label: "Stock at site", count: data.stock.length },
    { key: "deliveries", label: "Direct deliveries", count: data.deliveries.length },
    { key: "transfers", label: "Transfers", count: data.transfers.length },
    { key: "issues", label: "Issued to work", count: data.issues.length },
    { key: "surplus", label: "Surplus board" },
    { key: "requests", label: "Transfer requests" },
  ];

  return (
    <div className="space-y-6" data-testid="site-inventory-page">
      <PageHeader
        title="Site Inventory"
        description="Material stored at this project's site, and everything delivered straight to it."
        action={noStore && <Button onClick={createStore} disabled={creating} data-testid="create-site-store-button">{creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <WarehouseIcon className="h-4 w-4" />} Create site store</Button>}
      />
      <PageStatCards loading={loading} stats={[
        { label: "Items in stock", value: s.items_in_stock, color: "blue" },
        { label: "Stock value", value: inr(s.stock_value), color: "green" },
        { label: "Delivered (30 days)", value: inr(s.delivered_30d), color: "orange" },
        { label: "Issued to work", value: inr(s.issued_value), color: "burgundy" },
      ]} />

      {noStore && (
        <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground" data-testid="no-site-store">
          This project has no site store yet. Create one to keep delivered material as site stock: on a GRN, choose this site store as the destination. Direct deliveries that are used at once are listed below without it.
        </div>
      )}
      {!noStore && (
        <p className="text-xs text-muted-foreground">Site store: {data.stores.map((x) => `${x.name} (${x.code})`).join(", ")}</p>
      )}

      <div className="rounded-xl border border-border bg-card">
        <TabFilter tabs={tabs} active={tab} onChange={setTab} />
        {tab === "stock" && (
          <Grid empty="No stock at this site yet. Receive a GRN into the site store, or transfer material in."
            heads={[{ label: "Store" }, { label: "Item" }, { label: "Unit" }, { label: "Qty", right: true }, { label: "Avg rate", right: true }, { label: "Value", right: true }, { label: "" }]}
            rows={data.stock.map((r) => [r.store, `${r.item_code} · ${r.item_name}`, r.uom || "—", qty(r.qty), inr(r.avg_cost), inr(r.value), <span key="a" className="flex items-center justify-end gap-2">{r.low_stock && <Badge variant="destructive">Low</Badge>}<Button size="sm" variant="outline" onClick={() => setIssuing(r)} data-testid={`issue-to-work-${r.item_code}`}><PackageMinus className="h-3.5 w-3.5" /> Issue to work</Button><Button size="sm" variant="outline" onClick={() => setOffering(r)} data-testid={`offer-surplus-${r.item_code}`}><Share2 className="h-3.5 w-3.5" /> Offer to sites</Button></span>])} />
        )}
        {tab === "deliveries" && (
          <Grid empty="No direct deliveries yet. A posted GRN on this project's PO that has no warehouse, or goes into the site store, appears here."
            heads={[{ label: "Date" }, { label: "GRN" }, { label: "PO" }, { label: "Supplier" }, { label: "Material" }, { label: "Unit" }, { label: "Qty", right: true }, { label: "Rate", right: true }, { label: "Value", right: true }, { label: "Handling" }]}
            rows={data.deliveries.map((r) => [dmy(r.date), r.grn_number, r.po_number, r.vendor || "—", r.description, r.uom || "—", qty(r.qty), inr(r.rate), inr(r.value),
              <Badge key="k" variant="outline" className={`gap-1.5 font-medium ${(KIND_STYLE[r.kind] || KIND_STYLE["Used at once"]).cls}`} title={(KIND_STYLE[r.kind] || {}).tip}><span className="inline-block h-1.5 w-1.5 rounded-full bg-current" />{r.kind}</Badge>])} />
        )}
        {tab === "transfers" && (
          <Grid empty="No transfers in or out of this site store yet."
            heads={[{ label: "Date" }, { label: "Direction" }, { label: "Site store" }, { label: "From / to" }, { label: "Item" }, { label: "Unit" }, { label: "Qty", right: true }, { label: "Value", right: true }]}
            rows={data.transfers.map((r) => [dmy(r.date), <Badge key="d" variant={r.direction === "IN" ? "default" : "secondary"}>{r.direction === "IN" ? "Received" : "Sent"}</Badge>, r.store,
              r.other_store ? (r.direction === "IN" ? `from ${r.other_store}` : `to ${r.other_store}`) : "—", `${r.item_code} · ${r.item_name}`, r.uom || "—", qty(r.qty), inr(r.value)])} />
        )}
        {tab === "surplus" && <SurplusBoardTab projectId={projectId} onChanged={load} />}
        {tab === "requests" && <SurplusRequestsTab projectId={projectId} onChanged={load} />}
        {tab === "issues" && (
          <Grid empty="Nothing issued to work yet. Issue material from Inventory → Goods Issues."
            heads={[{ label: "Date" }, { label: "Issue no." }, { label: "From store" }, { label: "Item" }, { label: "Unit" }, { label: "Qty", right: true }, { label: "Value", right: true }, { label: "Work item" }]}
            rows={data.issues.map((r) => [dmy(r.date), r.number || "—", r.store, `${r.item_code} · ${r.item_name}`, r.uom || "—", qty(r.qty), inr(r.value), r.wbs || "—"])} />
        )}
      </div>
      <OfferDialog row={offering} projectId={projectId} onClose={() => setOffering(null)} onDone={() => { setOffering(null); setTab("requests"); load(); }} />
      <IssueDialog row={issuing} projectId={projectId} onClose={() => setIssuing(null)} onDone={() => { setIssuing(null); setTab("issues"); load(); }} />
    </div>
  );
}

function IssueDialog({ row, projectId, onClose, onDone }) {
  const [quantity, setQuantity] = useState("");
  const [wbsId, setWbsId] = useState("");
  const [date, setDate] = useState(todayISO());
  const [remarks, setRemarks] = useState("");
  const [saving, setSaving] = useState(false);
  useResetOnClose(Boolean(!!row), () => { setQuantity(""); setWbsId(""); setRemarks(""); });      // closed without saving: opens fresh next time
  useEffect(() => { if (row) { setQuantity(""); setWbsId(""); setDate(todayISO()); setRemarks(""); } }, [row]);
  const available = row ? Number(row.qty) : 0;
  const q = Number(quantity);
  const bad = !(q > 0) || q > available;
  const submit = async () => {
    if (bad) return;
    setSaving(true);
    let created = null;
    try {
      const { data: gi } = await api.post("/inventory/goods-issues", {
        company_id: row.company_id, warehouse_id: row.store_id, project_id: projectId, wbs_item_id: wbsId || null,
        issue_date: date, remarks: remarks || null, items: [{ item_id: row.item_id, quantity: q }],
      });
      created = gi;
      await api.post(`/inventory/goods-issues/${gi.id}/post`);
      toast.success(`Issued ${q} ${row.item_name} to work (${gi.number})`);
      onDone();
    } catch (err) {
      toast.error(created ? `${getErrorMessage(err)} — the issue ${created.number} was saved as a draft in Inventory → Goods Issues` : getErrorMessage(err));
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="issue-to-work-dialog">
        {row && (<>
          <DialogHeader><DialogTitle>Issue to work</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="rounded-md bg-muted/50 p-2.5 text-xs"><div className="font-medium text-foreground">{row.item_code} · {row.item_name}</div><div className="text-muted-foreground">From {row.store} — available {qty(row.qty)} {row.uom || ""} at {inr(row.avg_cost)} each</div></div>
            <div><Label>Quantity</Label><Input type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="mt-1.5" data-testid="issue-qty-input" />{q > available && <p className="mt-1 text-xs text-rose-500">More than the stock available ({qty(row.qty)}).</p>}</div>
            <div><Label>Work activity</Label><div className="mt-1.5"><WbsItemSelect projectId={projectId} value={wbsId} onChange={setWbsId} testId="issue-wbs-select" /></div><p className="mt-1 text-xs text-muted-foreground">Pick the activity that uses it, so the cost lands there. Left blank, the cost goes to the project as a whole.</p></div>
            <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Remarks (optional)</Label><Input value={remarks} onChange={(e) => setRemarks(e.target.value)} className="mt-1.5" /></div>
            {!bad && <p className="text-xs text-muted-foreground">Cost booked to the project: {inr(q * Number(row.avg_cost))}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button disabled={bad || saving} onClick={submit} data-testid="issue-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Issue &amp; post</Button>
          </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
